import * as React from 'react';

import type { ToastActionElement, ToastProps } from '@/shared/ui/Toast/Toast';

const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
    id: string;
    title?: React.ReactNode;
    description?: React.ReactNode;
    action?: ToastActionElement;
    onClose?: () => void;
};

type Toast = Omit<ToasterToast, 'id'>;

type State = {
    toasts: ToasterToast[];
};

type Action =
    | { type: 'ADD_TOAST'; toast: ToasterToast }
    | { type: 'UPDATE_TOAST'; toast: Partial<ToasterToast> }
    | { type: 'DISMISS_TOAST'; toastId?: string }
    | { type: 'REMOVE_TOAST'; toastId?: string };

const listeners: Array<(state: State) => void> = [];
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const activeToastKeys = new Set<string>();
let memoryState: State = { toasts: [] };
let count = 0;

function genId() {
    count = (count + 1) % Number.MAX_SAFE_INTEGER;
    return count.toString();
}

function getToastKey(toast: Pick<Toast, 'title' | 'description'>) {
    return `${toast.title ?? ''}-${toast.description ?? ''}`;
}

function addToRemoveQueue(toastId: string) {
    if (toastTimeouts.has(toastId)) return;

    const timeout = setTimeout(() => {
        toastTimeouts.delete(toastId);
        dispatch({ type: 'REMOVE_TOAST', toastId });
    }, TOAST_REMOVE_DELAY);

    toastTimeouts.set(toastId, timeout);
}

function dispatch(action: Action) {
    memoryState = reducer(memoryState, action);
    listeners.forEach((l) => l(memoryState));
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'ADD_TOAST':
            return {
                toasts: [action.toast, ...state.toasts],
            };

        case 'UPDATE_TOAST':
            return {
                toasts: state.toasts.map((t) =>
                    t.id === action.toast.id ? { ...t, ...action.toast } : t,
                ),
            };

        case 'DISMISS_TOAST': {
            const toastsToDismiss = action.toastId
                ? state.toasts.filter((t) => t.id === action.toastId)
                : state.toasts;

            toastsToDismiss.forEach((t) => {
                addToRemoveQueue(t.id);
                activeToastKeys.delete(getToastKey(t));
            });

            return {
                toasts: state.toasts.map((t) =>
                    !action.toastId || t.id === action.toastId ? { ...t, open: false } : t,
                ),
            };
        }

        case 'REMOVE_TOAST':
            return {
                toasts: action.toastId ? state.toasts.filter((t) => t.id !== action.toastId) : [],
            };
    }
}

function toast({ onClose, ...props }: Toast) {
    const key = getToastKey(props);
    if (activeToastKeys.has(key)) return;

    const id = genId();
    activeToastKeys.add(key);

    const dismiss = () => {
        onClose?.();
        dispatch({ type: 'DISMISS_TOAST', toastId: id });
        activeToastKeys.delete(key);
    };

    dispatch({
        type: 'ADD_TOAST',
        toast: {
            ...props,
            id,
            open: true,
            onClose: onClose || dismiss,
            onOpenChange: (open) => {
                if (!open) dismiss();
            },
        },
    });

    const update = (newProps: Partial<ToasterToast>) =>
        dispatch({ type: 'UPDATE_TOAST', toast: { ...newProps, id } });

    return { id, dismiss, update };
}

function useToast() {
    const [state, setState] = React.useState<State>(memoryState);

    React.useEffect(() => {
        listeners.push(setState);
        return () => {
            const index = listeners.indexOf(setState);
            if (index > -1) listeners.splice(index, 1);
        };
    }, []);

    return {
        ...state,
        toast,
        dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
    };
}

export { useToast, toast };
